/**
 * Screenshot of a TLDraw sketch
 */

import type { FileUIPart } from "ai";
import { MAX_IMAGE_EDGE_PX } from "playground/agents/model-images";
import { Box, FileHelpers, type BoxModel, type Editor } from "tldraw";
import { invariant } from "ts-invariant";

import type { ScreenshotPart } from "../tldraw";

/** An attachment on the user's message, like one they picked themselves: the
 *  intake, the transcript and the model conversion all already carry those.
 *  The media type is the data URL's own, since a browser without a WebP
 *  encoder hands back a PNG. */
export const convertScreenshot = ({
  screenshot,
}: ScreenshotPart): FileUIPart | undefined => {
  if (!screenshot) return undefined;
  const mediaType = /^data:([^;,]+)[;,]/.exec(screenshot)?.[1];
  invariant(mediaType, "sketch screenshot must be a data URL");
  return { type: "file", mediaType, url: screenshot };
};

export const screenshotPartBuilder = async (
  editor: Editor,
  bounds: BoxModel,
) => {
  const contextBounds = bounds;

  const contextBoundsBox = Box.From(contextBounds);

  const shapes = editor.getCurrentPageShapesSorted().filter((shape) => {
    if (!editor) return false;
    const bounds = editor.getShapeMaskedPageBounds(shape);
    if (!bounds) return false;
    return contextBoundsBox.includes(bounds);
  });

  if (shapes.length === 0) {
    return { type: "screenshot", screenshot: null };
  }

  // A screenshot rides on the user's message, where it is never dropped, and
  // one arrives per turn: past twenty of them the request is only legal if
  // each fits the cap.
  const largestDimension = Math.max(bounds.w, bounds.h);
  const scale = Math.min(1, MAX_IMAGE_EDGE_PX / largestDimension);

  const result = await editor.toImage(shapes, {
    format: "webp",
    background: true,
    bounds: Box.From(bounds),
    padding: 0,
    pixelRatio: 1,
    scale,
  });

  return {
    type: "screenshot",
    screenshot: await FileHelpers.blobToDataUrl(result.blob),
  };
};
